import imageCompression, { Options } from 'browser-image-compression';
import { clsx } from 'clsx';
import { ChangeEventHandler, HTMLAttributes, useCallback, useRef, useState } from 'react';

import { CloseIcon } from '@/components/icons/modal';
import { Loader } from '@/components/Loader/Loader.tsx';
import { isAndroidApp } from '@/utils';

import cn from './styles.module.sass';

interface IFileInputProps extends HTMLAttributes<HTMLInputElement> {
    name?: string;
    imgSrc?: string;
    onFileCompression?: (file: File | null) => void;
}

export const FileInput = ({ name, imgSrc, onFileCompression, ...props }: IFileInputProps) => {
    const [fileUrl, setFileUrl] = useState<string>('');
    const [isCompressing, setIsCompressing] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
        async e => {
            if (props.onChange) {
                props.onChange(e);
            }

            const imageFile = e.target.files?.[0];

            const compressImg = async (img: File) => {
                const options: Options = {
                    maxSizeMB: 5,
                    useWebWorker: true,
                    fileType: 'image/jpeg',
                };

                const compressedImage = await imageCompression(img, options);

                setFileUrl(URL.createObjectURL(compressedImage));

                if (onFileCompression) {
                    onFileCompression(compressedImage);
                }
            };

            if (imageFile) {
                setIsCompressing(true);

                try {
                    await compressImg(imageFile);
                } catch (_e) {
                    setFileUrl(URL.createObjectURL(imageFile));

                    if (onFileCompression) {
                        onFileCompression(imageFile);
                    }
                } finally {
                    setIsCompressing(false);
                }
            }
        },
        [onFileCompression]
    );

    const handleRemoveFile = () => {
        setFileUrl('');
        if (onFileCompression) {
            onFileCompression(null);
        }
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    return (
        <div className={cn.container}>
            {fileUrl && !isCompressing && (
                <div className={cn.uploadedImg}>
                    <img src={fileUrl} alt="" />
                    <div onClick={handleRemoveFile} className={cn.closeBtn}>
                        {CloseIcon}
                    </div>
                </div>
            )}
            {isCompressing && <Loader mini />}
            <label className={clsx((fileUrl || isCompressing) && cn.noWidth)}>
                {fileUrl.length === 0 && !isCompressing && (
                    <>
                        <h4 className={cn.name}>{name}</h4>
                        <div className={cn.imgWrapper}>
                            <img src={imgSrc} alt="" />
                        </div>
                        <span className={cn.callToAction}>Нажмите для загрузки</span>
                    </>
                )}

                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*, .heic, .heif"
                    {...props}
                    onChange={handleChange}
                    capture={isAndroidApp() ? "environment" : undefined}
                />
            </label>
        </div>
    );
};
